//! WARNING: AppWrapper includes GlobalStyle and has to be imported first, so styled-components places it as a first css definition inside the <style /> tag.
import {
  FunctionComponent,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react'

import { QueryClient } from '@tanstack/react-query'
import { AppProps } from 'next/app'
import dynamic from 'next/dynamic'
import { useRouter } from 'next/router'

import {
  disableStructuralSharingForQueries,
  getDehydratedQueryStateWithAdjustedTimestamp,
  defaultConfig as queryConfig,
} from '@utils/react-query'
import { initRegistry } from '@utils/registry'
import AppWrapper from '@components/AppWrapper'
import BlickPlusOfferPage from '@components/Page/BlickPlusOfferPage'
import DefaultPage from '@components/Page/DefaultPage'
import ErrorPage from '@components/Page/ErrorPage'
import NonErrorPagePreCSSComponents from '@components/Page/NonErrorPagePreCSSComponents'

import { CatchAllPageServerProps } from './[[...pageUrl]]'

interface MyAppProps extends AppProps {
  pageProps: CatchAllPageServerProps
}

const StandaloneWrapper = dynamic(
  () => import('@components/StandaloneWrapper'),
  { ssr: false },
)

initRegistry()

const MyApp: FunctionComponent<MyAppProps> = ({ Component, pageProps }) => {
  const router = useRouter()

  //! Force disable prefetching for all pages (this can be removed when migrating to App Router)
  router.prefetch = () => Promise.resolve()

  const handlePageShow = useCallback(
    (event: PageTransitionEvent) => {
      if (event.persisted) {
        // Simulate a client-side navigation to the current page
        // This re-triggers Next.js lifecycle (getInitialProps, effects, etc.)
        router.replace(router.asPath)
      }
    },
    [router],
  )

  const [queryClient] = useState(() => new QueryClient(queryConfig))

  disableStructuralSharingForQueries(queryClient)

  const {
    dehydratedQueryState,
    isError,
    isBlickPlusOfferPage,
    isUserProfilePage,
    keysThatShouldNotOverwriteClientCacheIfExists,
    ...restOfPageProps
  } = pageProps

  const isErrorPage = isError !== false

  const dehydratedQueryStateWithAdjustedTimestamp = useMemo(
    () =>
      isErrorPage
        ? dehydratedQueryState
        : getDehydratedQueryStateWithAdjustedTimestamp({
            queryClient,
            dehydratedQueryState,
            keysThatShouldNotOverwriteClientCacheIfExists,
          }),
    [
      queryClient,
      isErrorPage,
      dehydratedQueryState,
      keysThatShouldNotOverwriteClientCacheIfExists,
    ],
  )

  const PageComponent = isErrorPage
    ? ErrorPage
    : isBlickPlusOfferPage
      ? BlickPlusOfferPage
      : DefaultPage

  useEffect(() => {
    window.addEventListener('pageshow', handlePageShow)
    return () => window.removeEventListener('pageshow', handlePageShow)
  }, [handlePageShow])

  if (
    (pageProps as unknown as { isStandalonePage: boolean })?.isStandalonePage
  ) {
    return (
      <StandaloneWrapper
        dehydratedQueryStateWithAdjustedTimestamp={
          dehydratedQueryStateWithAdjustedTimestamp
        }
        queryClient={queryClient}
      >
        <Component />
      </StandaloneWrapper>
    )
  }

  return (
    <AppWrapper
      dehydratedQueryStateWithAdjustedTimestamp={
        dehydratedQueryStateWithAdjustedTimestamp
      }
      queryClient={queryClient}
      isErrorPage={isErrorPage}
      preCSSComponents={
        isErrorPage ? null : (
          <NonErrorPagePreCSSComponents
            isBlickPlusOfferPage={isBlickPlusOfferPage}
            isUserProfilePage={isUserProfilePage}
          />
        )
      }
    >
      <PageComponent Component={Component} restOfPageProps={restOfPageProps} />
    </AppWrapper>
  )
}

export default MyApp
